/**
 * Chrome-dimming state for the studio (the `.oc-interacting`
 * fade on the mobile dock + selection pill).
 *
 * The defect (Princewill, 2026-11-04: the dock "is stuck looking
 * faint"): dimming used to be ONE shared boolean, set true by
 * object:moving / pinch start and set false only by
 * object:modified / mouse:up / pinch end. Any gesture that ended
 * WITHOUT one of those events - a touch drag interrupted by a
 * system gesture or notification (touchcancel fires neither
 * modified nor mouse:up in fabric 5.5), a mouse released outside
 * the window, or a canvas disposed mid-gesture when the studio
 * re-initializes on a dimension change (its cleanup removed the
 * listeners without clearing the flag) - orphaned the true
 * forever, and the dock stayed at 15% opacity with pointer
 * events off for the rest of the session.
 *
 * This state machine makes the fade un-losable:
 * - Per-SOURCE flags (front drag, back drag, pinch): one source
 *   ending can no longer clear another's live gesture, and each
 *   source's own cleanup clears exactly its flag.
 * - set() is idempotent per source (object:moving fires dozens
 *   of times per drag; it must not inflate anything).
 * - reset() is the belt-and-braces clear wired to window blur
 *   and page hide: no gesture can be live in a blurred/hidden
 *   page, so any flag still up is by definition leaked.
 * - reevaluate() runs on the next touchstart / pointer end and
 *   drops any flag whose gesture is not physically live (the
 *   canvas reports no in-flight transform; a pinch needs two
 *   touches down) - so even a leak that somehow survives self-
 *   heals at the user's next touch instead of haunting the dock.
 */

export type InteractSource = 'front' | 'back' | 'pinch';

export class InteractFlags {
    private flags: Record<InteractSource, boolean> = { front: false, back: false, pinch: false };

    /** Mark a source live/dead. Returns the resulting dim state. */
    set(source: InteractSource, on: boolean): boolean {
        this.flags[source] = on;
        return this.any();
    }

    /** Is any source live (should the chrome be dimmed)? */
    any(): boolean {
        return this.flags.front || this.flags.back || this.flags.pinch;
    }

    /** Hard clear - for blur / page-hide, where no gesture can live. */
    reset(): boolean {
        this.flags.front = false;
        this.flags.back = false;
        this.flags.pinch = false;
        return false;
    }

    /**
     * Drop flags whose gesture is not physically live right now.
     * `touches` is the number of active touch points, or null to
     * leave the pinch flag alone (pointer-end events cannot see
     * the remaining touch count reliably).
     */
    reevaluate(live: { frontTransform: boolean; backTransform: boolean; touches: number | null }): boolean {
        if (!live.frontTransform) this.flags.front = false;
        if (!live.backTransform) this.flags.back = false;
        if (live.touches !== null && live.touches < 2) this.flags.pinch = false;
        return this.any();
    }

    /** Current flags (tests / diagnostics). */
    snapshot(): Record<InteractSource, boolean> {
        return { ...this.flags };
    }
}
